import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SketchPicker, ColorResult } from 'react-color';
import './ColorPicker.css';

interface ColorPickerProps {
  /** Current color value (hex string, e.g. "#ff0000") */
  value: string;
  /** Called when the color changes (on picker close or preset click) */
  onChange: (color: string) => void;
  /** Whether to show the hex value next to the swatch */
  showValue?: boolean;
  /** Preset colors to show in the picker */
  presetColors?: string[];
  /** Width of the picker panel */
  pickerWidth?: string;
  /** Custom className for the wrapper */
  className?: string;
}

interface PopoverPosition {
  top: number;
  left: number;
}

const DEFAULT_PRESETS: string[] = ['#ffffff', '#000000'];

const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  showValue = false,
  presetColors = DEFAULT_PRESETS,
  pickerWidth = '200px',
  className,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [tempColor, setTempColor] = useState<string>(value);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition>({ top: 0, left: 0 });
  const swatchRef = useRef<HTMLDivElement>(null);

  // Calculate popover position when opening
  useEffect(() => {
    if (isOpen && swatchRef.current) {
      const rect = swatchRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const pickerHeight = 310; // Estimated height of SketchPicker with presets
      const pickerWidthNum = parseInt(pickerWidth);
      
      // If not enough space below, and enough space above, show above
      const spaceBelow = viewportHeight - rect.bottom;
      const showAbove = spaceBelow < pickerHeight && rect.top > pickerHeight;

      // Adjust left if it would go off screen
      const leftPos = Math.min(rect.left, viewportWidth - pickerWidthNum - 16);

      setPopoverPosition({
        top: showAbove ? rect.top - pickerHeight - 8 : rect.bottom + 4,
        left: Math.max(8, leftPos), // Keep at least 8px from left edge
      });
    }
  }, [isOpen, pickerWidth]);

  const handleSwatchClick = useCallback((): void => {
    if (!isOpen) {
      setTempColor(value);
    }
    setIsOpen((prev) => !prev);
  }, [isOpen, value]);

  const handlePickerChange = useCallback((color: ColorResult): void => {
    setTempColor(color.hex);
  }, []);

  const handleClose = useCallback((): void => {
    setIsOpen(false);
    if (tempColor !== value) {
      onChange(tempColor);
    }
  }, [tempColor, value, onChange]);

  const handlePanelClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
  }, []);

  const wrapperClass = showValue
    ? `color-picker color-picker-with-value ${className ?? ''}`
    : `color-picker ${className ?? ''}`;

  return (
    <div className={wrapperClass.trim()}>
      <div
        ref={swatchRef}
        className="color-picker-swatch"
        style={{ backgroundColor: value }}
        onClick={handleSwatchClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSwatchClick();
          }
        }}
        aria-label="Pick color"
      />

      {showValue && <span className="color-picker-value">{value}</span>}

      {isOpen && createPortal(
        <div className="color-picker-portal">
          <div className="color-picker-backdrop" onClick={handleClose} />
          <div
            className="color-picker-panel"
            style={{ top: popoverPosition.top, left: popoverPosition.left }}
            onClick={handlePanelClick}
          >
            <SketchPicker
              color={tempColor}
              onChange={handlePickerChange}
              disableAlpha={true}
              width={pickerWidth}
              presetColors={presetColors}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ColorPicker;

